import React, { useMemo, useState, JSX } from 'react';
import {
  ApiFilter,
  ApiFilterCategory,
  ApiGroupedMultipleSelectionFilter,
  ApiMultipleSelectionFilter,
  ApiNumberRangeFilter,
  ApiSingleSelectionFilter
} from '@ibe/api';
import { Button, Col, Collapse, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faTimes } from '@fortawesome/free-solid-svg-icons';
import Keys from '../../../_Translations/generated/service-list.en.t.json.keys';
import fallback from '../../../_Translations/generated/service-list.en.t.json';
import FilterDiscard from './FilterDiscard';
import FilterComponent from './FilterComponent';
import useTranslation from '../../Hooks/useTranslation';
import AdditionalContent from './AdditionalContent';
import calculateAppliedFiltersCount, { CustomListOptions, FilterProperties } from './externals';
import useWindow from '../../Hooks/useWindow';
import { FilterOverrides } from '../../Components/DynamicList/FilterOptions/FilterOptions';

const FilterMobileView = ({
  filterProps,
  filtersFromApi,
  hasFilters,
  additionalContent,
  useIcons,
  customListOptions,
  filterOverrides,
  filterLabelTranslationMappings,
  showResultsCountInParenthesis,
  showResultsCount,
  showEmptyOptionsWhenFiltering,
  showResetButtonForFilter
}: {
  filterProps?: FilterProperties;
  filtersFromApi: ApiFilter[];
  hasFilters: boolean;
  additionalContent?: Array<{ id: number; component: JSX.Element }>;
  useIcons?: boolean;
  customListOptions?: CustomListOptions;
  filterOverrides?: FilterOverrides;
  filterLabelTranslationMappings?: Record<string, (label: string) => string>;
  showResultsCountInParenthesis?: boolean;
  showResultsCount?: Partial<Record<ApiFilterCategory, boolean>>;
  showEmptyOptionsWhenFiltering?: boolean;
  showResetButtonForFilter?: boolean;
}): JSX.Element => {
  const window = useWindow();
  const { t } = useTranslation('service-list', fallback, Keys);

  const [filterOpen, setFilterOpen] = useState(filterProps?.state.isOpen);

  const toggleFilterState = (): void => {
    const value = !filterOpen;
    setFilterOpen(value);
    if (filterProps && filterProps.state) {
      filterProps.state.onChange(value);
    }
  };

  const showEmptyOptionsWhenFilteringIsActive = useMemo(() => {
    if (showEmptyOptionsWhenFiltering) {
      let someFilterSelectionOptionIsActive = false;
      filtersFromApi.forEach((f: ApiFilter) => {
        switch (f.type) {
          case 'SingleSelectionFilter':
            const singleSelectionFilter = f as ApiSingleSelectionFilter;
            if (singleSelectionFilter.selectedOption) {
              someFilterSelectionOptionIsActive = true;
              return;
            }
            break;
          case 'GroupedMultipleSelectionFilter':
            const groupMultiFilter = f as ApiGroupedMultipleSelectionFilter;
            if (groupMultiFilter.selectedOptions) {
              someFilterSelectionOptionIsActive = true;
              return;
            }
            break;
          case 'MultipleSelectionFilter':
            const multiSelectFilter = f as ApiMultipleSelectionFilter;
            if (multiSelectFilter.selectedOptions) {
              someFilterSelectionOptionIsActive = true;
              return;
            }
            break;
          case 'NumberRangeFilter':
            const numberRangeFilter = f as ApiNumberRangeFilter;
            if (numberRangeFilter.maxSelected || numberRangeFilter.minSelected) {
              someFilterSelectionOptionIsActive = true;
              return;
            }
            break;
          default:
        }
      });
      return someFilterSelectionOptionIsActive;
    }
    return false;
  }, [filtersFromApi]);

  const renderFilterContent = (): JSX.Element => {
    return (
      <>
        <FilterDiscard filterProps={filterProps} />
        {filtersFromApi.map((f: ApiFilter, i: number) => (
          <FilterComponent
            key={JSON.stringify(f)}
            f={f}
            filterOrdinalNumber={i}
            isCollapsable={false}
            filterProps={filterProps}
            useIcons={useIcons}
            filterOverrides={filterOverrides}
            filterLabelTranslationMappings={filterLabelTranslationMappings}
            showResultsCountInParenthesis={showResultsCountInParenthesis}
            showResultsCount={showResultsCount}
            showEmptyOptionsWhenFiltering={showEmptyOptionsWhenFilteringIsActive}
            showResetButtonForFilter={showResetButtonForFilter}
          />
        ))}
        <AdditionalContent hasFilters={hasFilters} additionalContent={additionalContent} />
      </>
    );
  };

  return (
    <div className="w-100">
      <Button
        className="text-white btn-block my-2 font-weight-bold text-uppercase iso__serviceList__filter-button"
        color="primary"
        onClick={toggleFilterState}
      >
        <div className="justify-content-center">
          {customListOptions?.customIcons?.filter && (
            <FontAwesomeIcon icon={customListOptions?.customIcons?.filter} />
          )}
          <span className="mr-2">
            {t(Keys.filter)}{' '}
            <span className={'filters-count'}>{calculateAppliedFiltersCount(filterProps)}</span>
          </span>
          <span>
            {!filterOpen && <FontAwesomeIcon icon={faAngleDown} className="fas fa-lg" />}
            {filterOpen && <FontAwesomeIcon icon={faAngleUp} className="fas fa-lg" />}
          </span>
        </div>
      </Button>

      {customListOptions?.showFilterInModal ? (
        <Modal
          className="filter__mobile"
          isOpen={filterOpen}
          container={window?.document?.getElementById('iso') || undefined}
        >
          <ModalHeader className="d-flex justify-content-end">
            <FontAwesomeIcon
              icon={faTimes}
              onClick={toggleFilterState}
              style={{ cursor: 'pointer' }}
            />
          </ModalHeader>
          <ModalBody>
            {
              <>
                {renderFilterContent()}
                <div className={'filter__mobile__apply-button'}>
                  <Button onClick={toggleFilterState}>{t(Keys.apply)}</Button>
                </div>
              </>
            }
          </ModalBody>
        </Modal>
      ) : (
        <Collapse isOpen={filterOpen}>
          <Col className="border border-dark pb-1">{renderFilterContent()}</Col>
        </Collapse>
      )}
    </div>
  );
};

export default FilterMobileView;
